"use client";

import Link from "next/link";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import { useEffect, useState } from "react";
import { readAdvertisingConsent } from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";

const ATTRIBUTION_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_offer",
  "ref",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
] as const;

export function withCurrentAttribution(href: string): string {
  if (
    typeof window === "undefined" ||
    readAdvertisingConsent() !== "accepted" ||
    !window.location.search ||
    href.startsWith("#")
  ) {
    return href;
  }

  try {
    const currentParams = new URLSearchParams(window.location.search);
    const target = new URL(href, window.location.origin);
    let changed = false;

    for (const key of ATTRIBUTION_PARAM_KEYS) {
      const value = currentParams.get(key);
      if (!value || target.searchParams.has(key)) continue;
      target.searchParams.set(key, value);
      changed = true;
    }

    if (!changed) return href;

    if (target.origin === window.location.origin) {
      return `${target.pathname}${target.search}${target.hash}`;
    }

    return target.toString();
  } catch {
    return href;
  }
}

interface AttributedLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children: ReactNode;
}

export function AttributedLink({
  href,
  children,
  ...props
}: AttributedLinkProps) {
  const [trackedHref, setTrackedHref] = useState(href);

  useEffect(() => {
    const refreshHref = () => setTrackedHref(withCurrentAttribution(href));
    refreshHref();
    return subscribeToBrowserConsentCategory("advertising", refreshHref);
  }, [href]);

  return (
    <Link href={trackedHref} {...props}>
      {children}
    </Link>
  );
}
